(ns practitest.frontend.integration-settings.shared-form
  (:require [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components.selector2 :as selector2]))

(defn transform-project-for-selector [project]
  {:id         (keyword (:id project))
   :label      (:name project)
   :has-prefix (boolean (:has_prefix project))})

(defn filter-selected-projects [projects selected-project-ids]
  (set (filter #(contains? selected-project-ids (:id %)) projects)))

(defn get-sync-tooltip-text [entity-type bugtracker]
  (if (= entity-type :requirements)
    (format-translation (get-translation "bugtracker.integration-settings.requirements-sync.tooltip"
                                         "Enable synchronization between %{bugtracker} and PractiTest Requirements")
                        :bugtracker bugtracker)
    (format-translation (get-translation "bugtracker.integration-settings.issues-sync.tooltip"
                                         "Enable synchronization between %{bugtracker} and PractiTest Issues")
                        :bugtracker bugtracker)))

(defn get-entity-label [entity-type]
  (if (= entity-type :requirements) "requirement" "issue"))

(defn get-other-entity-type [entity-type]
  (if (= entity-type :requirements) :issues :requirements))

(defn get-pt-entity-label [entity-type]
  (if (= entity-type :requirements)
    (get-translation "requirement.singular" "Requirement")
    (get-translation "issue.singular" "Issue")))

(defn checkbox [{:keys [checked? on-change disabled? disabled-tooltip]}]
  [:input.m-checkbox
   {:type      "checkbox"
    :checked   checked?
    :disabled  disabled?
    :title     (when disabled? disabled-tooltip)
    :on-change on-change}])

(defn toggle-switch [{:keys [enabled? on-change disabled?]}]
  [:div.form-switch
   [:input.form-check-input
    {:type      "checkbox"
     :checked   enabled?
     :disabled  disabled?
     :on-change #(on-change (.. % -target -checked))}]])

;; Generic row used in both JIRA and Azure entity mapping sections.
;; Props:
;;   :project-name   - display name of the project (read-only)
;;   :type-options   - seq of {:id :label} for the type dropdown
;;   :selected-types - seq of selected type IDs
;;   :pt-entity      - display label for the PT entity (read-only)
;;   :placeholder    - placeholder text for the type selector
;;   :on-types-change - fn called with new vec of selected type IDs
;;   :on-toggle      - fn called when enable/disable link is clicked
;;   :disabled?      - whether this row is disabled
;;   :types-loading? - whether types are still being fetched
(defn project-mapping-row
  [{:keys [project-name type-options selected-types pt-entity placeholder
           on-types-change on-toggle disabled? types-loading? locked? locked-tooltip]}]
  (let [enable-disable-content
        (if disabled?
          [:<> [icons/mux-circle-plus] " " (get-translation "generic.label.enable" "Enable")]
          [:<> [icons/mux-circle-less] " " (get-translation "generic.label.disable" "Disable")])]
    [:div.mapping-row
     {:class (dom/classes-pairs :disabled disabled? :locked locked?)}
     [:span.locked-icon-slot
      (when locked?
        [tooltip/mux-tooltip
         {:position "top"}
         [:span locked-tooltip]
         (fn [tooltip-props]
           [:span.locked-icon tooltip-props [icons/mux-no-access]])])]
     [:input.form-control.project-name-input
      {:type     "text"
       :value    project-name
       :disabled true}]
     [:div.type-selector-wrapper
      [selector2/new-select
       {:multi?      true
        :disabled?   (or disabled? locked?)
        :tags?       false
        :value       (set (filter #(contains? (set selected-types) (:id %)) type-options))
        :items       type-options
        :key-fn      :id
        :label-fn    :label
        :placeholder (str placeholder "...")
        :on-change   (fn [selected-set]
                       (on-types-change (vec (map :id selected-set))))}]
      (when types-loading?
        [:span.type-selector-loading
         [spinners/mux-spinner {:size :small}]])]
     [icons/mux-up-down]
     [:input.form-control.pt-entity-input
      {:type     "text"
       :value    pt-entity
       :disabled true}]
     (if locked?
       [:span.disable-link.locked-link enable-disable-content]
       [:a.disable-link
        {:href     "#"
         :on-click (fn [e]
                     (.preventDefault e)
                     (on-toggle))}
        enable-disable-content])]))

(defn synchronization-section
  [{:keys [title enabled? other-entity-enabled? on-toggle projects current-mappings
           entity-type bugtracker mapping-item-component lists-loading?]}]
  [:div.sync-section
   [:div.sync-header
    [:div.sync-title
     [:h5 title]
     [tooltip/mux-tooltip
      {:position "top"}
      [:span (get-sync-tooltip-text entity-type bugtracker)]
      (fn [props]
        [:span.icon-info
         (assoc props :class "icon-info")
         [icons/mux-info-circle]])]]
    [toggle-switch {:enabled?  enabled?
                    :on-change on-toggle}]]
   (when enabled?
     [:<>
      [:div.project-mappings
       (for [project projects]
         ^{:key (name (:id project))}
         [mapping-item-component
          {:project               project
           :current-mappings      current-mappings
           :entity-type           entity-type
           :other-entity-enabled? other-entity-enabled?}])]
      (when lists-loading?
        [spinners/mux-spinner {:size :small :inside? false}])])])

(defn general-settings-section
  [{:keys [allow-issue-creation? use-pt-id? skip-mapping? bugtracker
           on-allow-issue-creation on-use-pt-id on-skip-mapping]}]
  [:div.general-settings
   [:h5 (get-translation "bugtracker.integration-settings.general-settings" "General Settings")]
   [:div.checkbox
    [:label
     [checkbox {:checked?  allow-issue-creation?
                :on-change (fn [e]
                             (on-allow-issue-creation (.. e -target -checked)))}]
     (get-translation "bugtracker.integration-settings.checkbox.issue-creation"
                      "Allow to create Issues in PractiTest that are not linked to Integration")]]
   [:div.checkbox
    [:label
     [checkbox {:disabled? (not allow-issue-creation?)
                :checked?  use-pt-id?
                :on-change (fn [e]
                             (on-use-pt-id (.. e -target -checked)))}]
     (get-translation "bugtracker.integration-settings.checkbox.use-pt-ids"
                      "Use PractiTest ID for synced Issues")]]
   [:div.checkbox
    [:label
     [checkbox {:checked?  skip-mapping?
                :on-change (fn [e]
                             (on-skip-mapping (.. e -target -checked)))}]
     (get-translation "bugtracker.integration-settings.checkbox.skip-mapping"
                      "Do not enforce mapping")]
    " "
    [tooltip/mux-tooltip
     {:position "top"}
     [:span (format-translation (get-translation "bugtracker.integration-settings.skip-mapping.tooltip"
                                                  "Selecting this option will disable restrictions for mapped entities. Any %{bugtracker} entity can be synced as a requirement or an issue.")
                                :bugtracker bugtracker)]
     (fn [props]
       [:span.icon-info
        (assoc props :class "icon-info")
        [icons/mux-info-circle]])]]])

(defn form-actions
  [{:keys [saving? dirty? on-cancel on-save]}]
  [:div.form-actions
   [buttons/button
    {:text      (get-translation "generic.button.cancel" "Cancel")
     :disabled? (or saving? (not dirty?))
     :on-click  on-cancel}]
   [buttons/button
    {:text      (if saving?
                  (str (get-translation "generic.label.saving" "Saving") "...")
                  (get-translation "jira-integration-settings.button.update-settings" "Update Settings"))
     :primary?  true
     :disabled? (or saving? (not dirty?))
     :on-click  on-save}]])
